import React, { useEffect, useState } from 'react';
import {
  AlertCircle,
  CheckCircle2,
  Loader2,
  RefreshCw,
  HelpCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  useOptimizationConfig,
  useRefreshHourlyProfile,
  useUpsertOptimizationConfig,
  type ConfigUpsertInput,
} from '@/hooks/optimization/useOptimizationSettings';

const DEFAULTS: Required<Omit<ConfigUpsertInput, 'forecast_source' | 'is_active'>> = {
  weight_fot: 1,
  weight_understaffing: 9999,
  weight_overtime: 1000,
  solver_timeout_seconds: 60,
};

interface ConfigTabProps {
  locationId: string | null;
}

export const ConfigTab: React.FC<ConfigTabProps> = ({ locationId }) => {
  const { data: config, isLoading } = useOptimizationConfig(locationId);
  const upsert = useUpsertOptimizationConfig(locationId);
  const refresh = useRefreshHourlyProfile();

  const [form, setForm] = useState(DEFAULTS);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [refreshResult, setRefreshResult] = useState<string | null>(null);

  useEffect(() => {
    if (config) {
      setForm({
        weight_fot: Number(config.weight_fot),
        weight_understaffing: Number(config.weight_understaffing),
        weight_overtime: Number(config.weight_overtime),
        solver_timeout_seconds: Number(config.solver_timeout_seconds),
      });
    }
    setSuccess(false);
    setError('');
  }, [config]);

  if (!locationId) {
    return (
      <p className="text-sm text-muted-foreground">
        Выберите локацию, чтобы настроить веса.
      </p>
    );
  }

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const submit = async () => {
    setError('');
    setSuccess(false);
    try {
      await upsert.mutateAsync(form);
      setSuccess(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ошибка сохранения');
    }
  };

  const doRefresh = async () => {
    if (!locationId) return;
    setRefreshResult(null);
    try {
      const r = await refresh.mutateAsync(locationId);
      const fallback = r.fallbackToUniform ? ' [fallback: равномерное]' : '';
      setRefreshResult(
        `Обновлено ${r.rowsUpserted} строк (7 дней × 24 часа = 168), ` +
          `данных за ${r.daysSampled} дней${fallback}`,
      );
    } catch (err) {
      setRefreshResult(
        `Ошибка: ${err instanceof Error ? err.message : 'unknown'}`,
      );
    }
  };

  return (
    <div className="space-y-6">
      <section className="space-y-2 rounded-md border border-purple-200 bg-purple-50 p-4">
        <h3 className="text-base font-medium">Hourly profile (обязательно)</h3>
        <p className="text-sm text-muted-foreground">
          Профиль распределения продаж по часам и дням недели — нужен для
          синтеза часового спроса из дневного прогноза. Пересчитывается на
          основе последних 30 дней истории <code>/sales/hourly</code> aqniet.
          Без него Smart Schedule не запустится — нажмите кнопку перед первой
          генерацией для каждой локации.
        </p>
        <div className="flex items-center gap-3 pt-2">
          <Button onClick={doRefresh} disabled={refresh.isPending}>
            {refresh.isPending ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            Обновить hourly profile
          </Button>
          {refreshResult && (
            <span className="text-sm text-muted-foreground">
              {refreshResult}
            </span>
          )}
        </div>
      </section>

      <section className="space-y-3">
        <div className="flex items-start gap-2">
          <h3 className="text-base font-medium">Веса целевой функции</h3>
          <HelpCircle className="h-4 w-4 text-muted-foreground mt-1" />
        </div>
        <p className="text-sm text-muted-foreground">
          Solver минимизирует <span className="font-mono">α·ФОТ + β·недокрытие
          + γ·переработки</span>. Чем больше вес, тем строже соответствующий
          критерий. ФОТ измеряется в KZT, недокрытие — в часах, переработки — в
          часах.
        </p>

        <div className="grid grid-cols-2 gap-4">
          <NumberField
            label="α — вес ФОТ"
            value={form.weight_fot}
            step="0.01"
            onChange={(v) => setForm({ ...form, weight_fot: v })}
            hint="по умолчанию 1.0"
          />
          <NumberField
            label="β — вес недокрытия"
            value={form.weight_understaffing}
            step="1"
            onChange={(v) => setForm({ ...form, weight_understaffing: v })}
            hint="должно быть достаточно большим (~9999), чтобы β·недокрытие конкурировало с α·ФОТ"
          />
          <NumberField
            label="γ — вес переработок"
            value={form.weight_overtime}
            step="1"
            onChange={(v) => setForm({ ...form, weight_overtime: v })}
            hint="по умолчанию 1000"
          />
          <NumberField
            label="Solver timeout (сек)"
            value={form.solver_timeout_seconds}
            min={5}
            max={600}
            step="5"
            onChange={(v) => setForm({ ...form, solver_timeout_seconds: v })}
            hint="до 600 сек"
          />
        </div>

        {success && (
          <Alert className="border-green-300 bg-green-50">
            <CheckCircle2 className="h-4 w-4 text-green-600" />
            <AlertDescription className="text-green-800">
              Конфигурация сохранена
            </AlertDescription>
          </Alert>
        )}
        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <div className="flex justify-end">
          <Button onClick={submit} disabled={upsert.isPending}>
            {upsert.isPending ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : null}
            Сохранить
          </Button>
        </div>
      </section>
    </div>
  );
};

const NumberField: React.FC<{
  label: string;
  value: number;
  min?: number;
  max?: number;
  step?: string;
  hint?: string;
  onChange: (v: number) => void;
}> = ({ label, value, min, max, step, hint, onChange }) => (
  <div>
    <Label>{label}</Label>
    <Input
      type="number"
      value={value}
      min={min}
      max={max}
      step={step}
      onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
    />
    {hint && <p className="text-xs text-muted-foreground mt-1">{hint}</p>}
  </div>
);
